import {
  fetchActivePlans,
  fetchPlans,
  selectFirstActivePlan,
  selectIsAccountOwner,
  selectPlanByActiveId,
} from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import { useQuery, useSelector } from "@app/react";
import { plansUrl } from "@app/routes";
import { useTrialNotice } from "../hooks/use-trial-notice";
import { Banner } from "./banner";
import { ButtonLink } from "./button";
import { IconAlertTriangle } from "./icons";
import { TopPlanBannerContactOwner } from "./top-plan-banner-contact-owner";

/**
 * Renders the top plan/trial banners. Can show up to two banners:
 * 1. Trial banner – when on a free trial with no payment method
 * 2. Development banner – when on the Development plan
 *
 * Each banner has two variants:
 * - Account owners: see "Upgrade plan" / "Upgrade to Production" CTA
 * - Non-owners: see TopPlanBannerContactOwner (contact an Account Owner to upgrade)
 */
export const TopPlanBanner = () => {
  const org = useSelector(selectOrganizationSelected);
  useQuery(fetchPlans());
  useQuery(fetchActivePlans({ orgId: org.id }));
  const activePlan = useSelector(selectFirstActivePlan);
  const storePlan = useSelector((s) =>
    selectPlanByActiveId(s, { id: activePlan.planId }),
  );
  const isAccountOwner = useSelector((s) =>
    selectIsAccountOwner(s, { orgId: org.id }),
  );
  const { hasTrialNoPayment, expiresIn } = useTrialNotice();

  const showTrialBanner = hasTrialNoPayment;
  const showDevelopmentBanner = storePlan.name === "development";

  if (!showTrialBanner && !showDevelopmentBanner) {
    return null;
  }

  return (
    <div className="flex flex-col">
      {showTrialBanner && (
        <Banner
          variant="warning"
          className="rounded-none text-sm"
          showIcon={false}
        >
          <div className="flex w-full items-center justify-center gap-4">
            <IconAlertTriangle
              color="#E9B64D"
              variant="base"
              className="shrink-0"
            />
            <span className="font-bold">
              You're on a free trial.
              {expiresIn ? ` Your trial ends ${expiresIn}.` : ""}
            </span>
            {isAccountOwner ? (
              <ButtonLink
                to={plansUrl()}
                variant="white"
                size="sm"
                className="shrink-0"
              >
                Upgrade plan
              </ButtonLink>
            ) : (
              <TopPlanBannerContactOwner />
            )}
          </div>
        </Banner>
      )}
      {showDevelopmentBanner && (
        <Banner
          variant="warning"
          className="rounded-none text-sm"
          showIcon={false}
        >
          <div className="flex w-full items-center justify-center gap-4">
            <IconAlertTriangle
              color="#E9B64D"
              variant="base"
              className="shrink-0"
            />
            <span className="font-bold">
              You're on the Development plan (not intended for production
              workloads or PHI).
            </span>
            {isAccountOwner ? (
              <ButtonLink
                to={plansUrl()}
                variant="white"
                size="sm"
                className="shrink-0"
              >
                Upgrade to Production
              </ButtonLink>
            ) : (
              <TopPlanBannerContactOwner />
            )}
          </div>
        </Banner>
      )}
    </div>
  );
};
